import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import SellOutlined from '@mui/icons-material/SellOutlined';
import WarehouseOutlined from '@mui/icons-material/WarehouseOutlined';
import {
  Alert,
  Box,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, unwrap, unwrapJson } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { SearchField } from '../../../components/DataTable';
import { EmptyState } from '../../../components/states';
import { moneyCompact } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { Donut } from '../components/charts';
import { ReportFrame } from '../components/ReportFrame';
import { ReportTable } from '../components/ReportTable';
import { Panel, QueryError, StatGrid, StatTile } from '../components/ui';
import { useReportParams, type ReportParams } from '../components/useReportParams';
import { fetchCsv, reportFileName } from '../lib/csv';
import { num } from '../lib/numbers';
import { ChartRow, ItemCell, M, Q } from './common';

/** Location filter (only when the user may list locations). */
function LocationSelect({ p }: { p: ReportParams }) {
  const { can } = useAuth();
  const allowed = can('inventory.view');
  const q = useQuery({
    queryKey: ['locations'],
    queryFn: () => unwrap(api.GET('/locations')),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });
  const locations = q.data?.data.filter((l) => l.isActive) ?? [];
  if (!allowed || locations.length < 2) return null;
  return (
    <TextField
      select
      size="small"
      fullWidth={false}
      label="Location"
      value={p.get('locationId') ?? ''}
      onChange={(e) => p.update({ locationId: e.target.value || null })}
      sx={{ minWidth: 200, bgcolor: 'background.paper', borderRadius: '10px' }}
      slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
    >
      <MenuItem value="">All locations</MenuItem>
      {locations.map((l) => (
        <MenuItem key={l.id} value={l.id}>
          {l.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

export function StockOnHandReport() {
  const p = useReportParams();
  const { format } = useShop();
  const locationId = p.get('locationId') ?? undefined;
  const search = p.get('q') ?? '';
  const includeZero = p.get('includeZero') === 'true' ? ('true' as const) : undefined;
  const query = {
    locationId,
    q: search || undefined,
    includeZero,
    page: p.page,
    pageSize: p.pageSize,
  };
  const q = useQuery({
    queryKey: ['reports', 'stock-on-hand', query],
    queryFn: () => unwrapJson(api.GET('/reports/stock-on-hand', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data;
  const value = q.data?.totals.value;
  return (
    <ReportFrame
      slug="stock-on-hand"
      period="Current stock"
      controls={
        <>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '0 1 320px' } }}>
            <SearchField
              value={search}
              onChange={(v) => p.update({ q: v || null })}
              placeholder="Search name or SKU…"
            />
          </Box>
          <LocationSelect p={p} />
          <FormControlLabel
            control={
              <Switch
                checked={includeZero === 'true'}
                onChange={(e) => p.update({ includeZero: e.target.checked ? 'true' : null })}
              />
            }
            label="Show zero stock"
          />
        </>
      }
      onExport={() =>
        fetchCsv(
          api.GET('/reports/stock-on-hand', {
            params: { query: { locationId, q: search || undefined, includeZero, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('stock-on-hand', p.today),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Items in stock"
          value={q.data?.page.total}
          hint={includeZero ? 'Including zero stock' : 'With a non-zero quantity'}
          icon={<Inventory2Outlined />}
          loading={q.isLoading}
        />
        <StatTile
          label="Stock value"
          value={value === null ? 'Hidden' : value !== undefined && moneyCompact(value, format)}
          hint={value === null ? 'Needs the view-cost permission' : 'At average cost'}
          icon={<SellOutlined />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      <ReportTable
        loading={q.isLoading}
        rows={rows}
        rowKey={(r) => r.variantId}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        empty={
          <EmptyState
            icon={<Inventory2Outlined />}
            title={search ? 'No matching items' : 'No stock on hand'}
            body={
              search
                ? 'Try a different name or SKU.'
                : 'Stock appears here once goods are received or opening stock is entered.'
            }
          />
        }
        columns={[
          { key: 'name', header: 'Item', render: (r) => <ItemCell name={r.name} sku={r.sku} /> },
          {
            key: 'onHand',
            header: 'On hand',
            align: 'right',
            render: (r) => <Q v={r.qtyOnHand} unit={r.unitSymbol} />,
          },
          {
            key: 'reserved',
            header: 'Reserved',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <Q v={r.qtyReserved} />,
          },
          {
            key: 'available',
            header: 'Available',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => (
              <Box
                component="span"
                sx={{ color: num(r.available) < 0 ? 'error.main' : undefined, fontWeight: 600 }}
              >
                <Q v={r.available} />
              </Box>
            ),
          },
          {
            key: 'avgCost',
            header: 'Avg cost',
            align: 'right',
            hideBelow: 'lg',
            render: (r) => <M v={r.avgCost} />,
          },
          {
            key: 'value',
            header: 'Value',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <M v={r.value} strong />,
            footer: value === null ? '—' : <M v={value} />,
          },
        ]}
      />
    </ReportFrame>
  );
}

export function LowStockReport() {
  const p = useReportParams();
  const locationId = p.get('locationId') ?? undefined;
  const query = { locationId, page: p.page, pageSize: p.pageSize };
  const q = useQuery({
    queryKey: ['reports', 'low-stock', query],
    queryFn: () => unwrapJson(api.GET('/reports/low-stock', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data;
  const outOfStock = (rows ?? []).filter((r) => num(r.available) <= 0).length;
  const covered = (rows ?? []).filter((r) => num(r.onOrder) >= num(r.shortfall)).length;
  return (
    <ReportFrame
      slug="low-stock"
      period="Current stock"
      controls={<LocationSelect p={p} />}
      onExport={() =>
        fetchCsv(
          api.GET('/reports/low-stock', {
            params: { query: { locationId, format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('low-stock', p.today),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Below reorder level"
          value={q.data?.page.total}
          icon={<ReportProblemOutlined />}
          tone="warning"
          loading={q.isLoading}
        />
        <StatTile
          label="Out of stock"
          value={outOfStock}
          hint="Among the items listed"
          icon={<Inventory2Outlined />}
          tone="error"
          loading={q.isLoading}
        />
        <StatTile
          label="Covered by orders"
          value={covered}
          hint="Open purchase orders cover the shortfall"
          icon={<CheckCircleOutlineRounded />}
          tone="success"
          loading={q.isLoading}
        />
      </StatGrid>
      <ReportTable
        loading={q.isLoading}
        rows={rows}
        rowKey={(r) => r.variantId}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        showTotals={false}
        empty={
          <EmptyState
            icon={<CheckCircleOutlineRounded />}
            title="Everything is well stocked"
            body="No item is at or below its reorder level right now."
          />
        }
        columns={[
          { key: 'name', header: 'Item', render: (r) => <ItemCell name={r.name} sku={r.sku} /> },
          {
            key: 'level',
            header: 'Stock vs reorder level',
            hideBelow: 'md',
            width: 220,
            render: (r) => {
              const level = num(r.reorderLevel);
              const ratio = level > 0 ? Math.max(0, Math.min(1, num(r.available) / level)) : 0;
              return (
                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={ratio * 100}
                    color={ratio <= 0 ? 'error' : 'warning'}
                    sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(ratio * 100)}% of reorder level
                  </Typography>
                </Box>
              );
            },
          },
          {
            key: 'available',
            header: 'Available',
            align: 'right',
            render: (r) => (
              <Box
                component="span"
                sx={{
                  fontWeight: 650,
                  color: num(r.available) <= 0 ? 'error.main' : 'warning.main',
                }}
              >
                <Q v={r.available} unit={r.unitSymbol} />
              </Box>
            ),
          },
          {
            key: 'reorder',
            header: 'Reorder at',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <Q v={r.reorderLevel} />,
          },
          {
            key: 'shortfall',
            header: 'Shortfall',
            align: 'right',
            hideBelow: 'sm',
            render: (r) => <Q v={r.shortfall} />,
          },
          {
            key: 'onOrder',
            header: 'On order',
            align: 'right',
            hideBelow: 'md',
            render: (r) => <Q v={r.onOrder} />,
          },
          {
            key: 'suggest',
            header: 'Suggested order',
            align: 'right',
            hideBelow: 'lg',
            render: (r) => (r.reorderQty ? <Q v={r.reorderQty} /> : '—'),
          },
        ]}
      />
    </ReportFrame>
  );
}

export function StockValuationReport() {
  const p = useReportParams();
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['reports', 'stock-valuation'],
    queryFn: () => unwrapJson(api.GET('/reports/stock-valuation')),
  });
  const d = q.data;
  const diff = num(d?.difference);
  return (
    <ReportFrame
      slug="stock-valuation"
      period="Current stock"
      onExport={() =>
        fetchCsv(
          api.GET('/reports/stock-valuation', {
            params: { query: { format: 'csv' } },
            parseAs: 'text',
          }),
          reportFileName('stock-valuation', p.today),
        )
      }
    >
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Stock value"
          value={d && moneyCompact(d.total, format)}
          hint="Quantity × average cost"
          icon={<WarehouseOutlined />}
          loading={q.isLoading}
        />
        <StatTile
          label="Inventory account"
          value={d && moneyCompact(d.inventoryAccountBalance, format)}
          hint="Balance in the books"
          icon={<SellOutlined />}
          tone="info"
          loading={q.isLoading}
        />
        <StatTile
          label="Difference"
          value={d && moneyCompact(d.difference, format)}
          hint={diff === 0 ? 'Books and stock agree' : 'Stock value minus the account'}
          icon={diff === 0 ? <CheckCircleOutlineRounded /> : <ReportProblemOutlined />}
          tone={diff === 0 ? 'success' : 'warning'}
          loading={q.isLoading}
        />
      </StatGrid>
      {d && diff !== 0 && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          Stock value and the Inventory account differ by <M v={d.difference} />. This usually means
          a manual journal entry touched the Inventory account, or stock moved without posting.
        </Alert>
      )}
      <ChartRow ratio="1fr 1.2fr">
        <Panel title="Value by location">
          <Donut
            loading={q.isLoading}
            centerLabel="Total"
            items={(d?.locations ?? []).map((l) => ({
              id: l.locationId,
              label: l.name,
              value: num(l.value),
            }))}
          />
        </Panel>
        <ReportTable
          loading={q.isLoading}
          rows={d?.locations}
          rowKey={(r) => r.locationId}
          empty={
            <EmptyState
              icon={<PlaceOutlined />}
              title="No stock to value"
              body="Value appears once goods are in stock."
            />
          }
          columns={[
            { key: 'name', header: 'Location', render: (r) => <ItemCell name={r.name} /> },
            {
              key: 'share',
              header: 'Share',
              align: 'right',
              render: (r) =>
                num(d?.total) ? `${((num(r.value) / num(d?.total)) * 100).toFixed(1)}%` : '—',
            },
            {
              key: 'value',
              header: 'Value',
              align: 'right',
              render: (r) => <M v={r.value} strong />,
              footer: <M v={d?.total} />,
            },
          ]}
        />
      </ChartRow>
    </ReportFrame>
  );
}
